import Image from 'next/image';
import { cn } from '@/lib/utils';
import type { BrandingConfig } from '../types';

/** The one bundled brand mark, used on every surface. */
const LOGO_SRC = '/images/icon2.png';

const SIZES = {
  sm: 26,
  md: 30,
  nav: 38, // app header — sits beside the theme-toggle button
  lg: 40,
  xl: 64, // lobby / left-state hero — the one place we size it up (§4.3)
} as const;

/**
 * The NSI brand mark. NSI Meet is a single-brand product, so this always
 * renders the bundled `/images/icon2.png` — never a typographic "NSI" wordmark,
 * and never an arbitrary tenant logo URL that could 404.
 *
 * The source PNG is a circular mark with a thin ring and a few % of transparent
 * margin baked into its 1024² canvas. Rendered raw that margin reads as "extra
 * space / a circle floating in a box". So we frame it in a `rounded-full`,
 * overflow-clipped box and bleed the artwork a hair past the edge (`scale`) so
 * the ring meets the frame — a crisp, perfectly-circular mark with no dead space,
 * identical on every surface. `branding.name` stays as alt text for a11y.
 */
export function TenantLogo({
  branding,
  size = 'md',
  priority = false,
  className,
}: {
  branding: BrandingConfig;
  size?: keyof typeof SIZES;
  /** Prioritise loading (above-the-fold marks like the header/lobby). */
  priority?: boolean;
  className?: string;
}) {
  const px = SIZES[size];
  return (
    <span
      className={cn('relative inline-block shrink-0 overflow-hidden rounded-full', className)}
      style={{ width: px, height: px }}
      aria-hidden={false}
    >
      <Image
        src={LOGO_SRC}
        alt={branding.name}
        width={px * 2}
        height={px * 2}
        priority={priority}
        draggable={false}
        className="h-full w-full scale-[1.05] object-cover select-none"
      />
    </span>
  );
}
